/*
 * <count-up>
 * Counts its number up from 0 once it scrolls into view. The section extracts the number from the
 * stat text in Liquid and keeps the text around it outside this element, e.g.
 * "3+" -> <count-up data-target="3">3</count-up>+
 *
 * Attributes:
 *   data-target="1000"     the final value (no thousands separators)
 *   data-decimals="1"      decimals to keep while counting, "4.5" counts 0.0 -> 4.5
 *   data-grouping          formats with thousands separators while counting, "1,000"
 *   data-duration="2000"   animation length in ms
 *
 * The final value is rendered by Liquid, so it shows as typed without JavaScript or with
 * `prefers-reduced-motion`.
 */
if (!customElements.get("count-up")) {
  class CountUp extends HTMLElement {
    connectedCallback() {
      this.target = parseFloat(this.dataset.target);
      if (Number.isNaN(this.target)) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      this.decimals = parseInt(this.dataset.decimals, 10) || 0;
      this.duration = parseInt(this.dataset.duration, 10) || 2000;
      this.finalText = this.textContent;

      this.render(0);

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.start();
        },
        { threshold: 0.5 },
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }

    start() {
      const startTime = performance.now();

      const step = (now) => {
        const progress = Math.min((now - startTime) / this.duration, 1);
        // easeOutCubic - fast start, slows down into the final value
        const eased = 1 - Math.pow(1 - progress, 3);

        if (progress < 1) {
          this.render(this.target * eased);
          this.frame = requestAnimationFrame(step);
        } else {
          this.textContent = this.finalText;
        }
      };

      this.frame = requestAnimationFrame(step);
    }

    render(value) {
      this.textContent = value.toLocaleString("en-US", {
        minimumFractionDigits: this.decimals,
        maximumFractionDigits: this.decimals,
        useGrouping: "grouping" in this.dataset,
      });
    }
  }

  customElements.define("count-up", CountUp);
}
